import { Alert, Button, Form, Input, InputNumber, Tooltip } from "antd";
import type { FormInstance } from "antd";
import { ArrowUpIcon, InfoIcon, PlusIcon, Trash2Icon } from "lucide-react";
import type { ChildAgeBand } from "@/lib/childPricing";

const MAX_CHILD_AGE_BANDS = 5;

/** Edits this activity's age bands without imposing tenant-wide age definitions. */
export function ChildAgeBands({ form }: { form: FormInstance }) {
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-medium">
          Child age bands <span className="font-normal text-slate-400">(optional)</span>
        </h3>
        <Tooltip title="Completed years, including both endpoints. Each activity can use different bands. Blank seasonal prices inherit regular prices. Missing regular age-band prices use the standard children or infant rate; zero means free.">
          <button
            type="button"
            aria-label="About child age bands and rates"
            className="inline-flex rounded text-slate-400 focus-visible:outline-2"
          >
            <InfoIcon size={14} />
          </button>
        </Tooltip>
        <span className="text-xs text-slate-500">Ages in completed years, inclusive (maximum 5 bands)</span>
      </div>
      <Form.List name="childAgeBands" rules={[{
        validator: async (_, bands: ChildAgeBand[] | undefined) => {
          if ((bands?.length ?? 0) > MAX_CHILD_AGE_BANDS) throw new Error("A maximum of 5 child age bands is allowed");
        },
      }]}>
        {(fields, { add, remove, move }, { errors }) => (
          <div className="flex flex-wrap items-center gap-2">
            {fields.map((field, index) => (
              <div
                key={field.key}
                className="flex max-w-full items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/60 p-2"
              >
                <Form.Item name={[field.name, "id"]} hidden>
                  <Input />
                </Form.Item>
                <Form.Item name={[field.name, "minAge"]} className="mb-0! w-16">
                  <InputNumber
                    min={0}
                    precision={0}
                    placeholder="From"
                    aria-label={`Age band ${index + 1} minimum age`}
                    className="w-full!"
                  />
                </Form.Item>
                <span className="text-slate-400" aria-hidden="true">
                  –
                </span>
                <Form.Item name={[field.name, "maxAge"]} className="mb-0! w-16">
                  <InputNumber
                    min={0}
                    precision={0}
                    placeholder="To"
                    aria-label={`Age band ${index + 1} maximum age`}
                    className="w-full!"
                  />
                </Form.Item>
                <span className="text-xs text-slate-500">yrs</span>
                <Tooltip title="Move band up">
                  <Button
                    type="text"
                    size="small"
                    disabled={index === 0}
                    aria-label={`Move age band ${index + 1} up`}
                    icon={<ArrowUpIcon size={14} />}
                    onClick={() => move(index, index - 1)}
                  />
                </Tooltip>
                <Tooltip title="Remove band">
                  <Button
                    type="text"
                    size="small"
                    aria-label={`Remove age band ${index + 1}`}
                    icon={<Trash2Icon size={14} />}
                    onClick={() => remove(field.name)}
                  />
                </Tooltip>
              </div>
            ))}
            <Button
              type="dashed"
              icon={<PlusIcon size={14} />}
              disabled={fields.length >= MAX_CHILD_AGE_BANDS}
              onClick={() => add({ id: crypto.randomUUID(), minAge: 0, maxAge: 5 })}
            >
              Add age band
            </Button>
            <Form.ErrorList errors={errors} />
          </div>
        )}
      </Form.List>
      <BandWarnings form={form} />
    </section>
  );
}

function BandWarnings({ form }: { form: FormInstance }) {
  const bands = (Form.useWatch("childAgeBands", form) || []) as ChildAgeBand[];
  const overlap = bands.some((band, index) =>
    bands.slice(0, index).some((other) => band.minAge <= other.maxAge && band.maxAge >= other.minAge),
  );
  const invalid = bands.some((band) => band.minAge == null || band.maxAge == null || band.minAge > band.maxAge);
  return overlap || invalid ? (
    <Alert
      type="warning"
      showIcon
      message={
        invalid
          ? "Incomplete or invalid bands are excluded from autofill."
          : "Bands overlap. Autofill uses the first matching band."
      }
    />
  ) : null;
}

/** Registers leaf prices for existing bands inside each regular or seasonal currency tariff. */
export function ChildAgeRateFields({ form, tariffName }: { form: FormInstance; tariffName: number }) {
  const bands = (Form.useWatch("childAgeBands", form) || []) as ChildAgeBand[];
  return (
    <>
      {bands.map((band) => (
        <Form.Item
          key={band.id}
          name={[tariffName, "childAgeRates", band.id]}
          label={`Age ${band.minAge ?? "…"}–${band.maxAge ?? "…"}`}
          className="mb-0! min-w-0 [&_.ant-form-item-label]:pb-1!"
        >
          <InputNumber
            min={0}
            placeholder="Not specified"
            aria-label={`Rate for ages ${band.minAge} to ${band.maxAge}`}
            className="w-full!"
          />
        </Form.Item>
      ))}
    </>
  );
}

/** Compact rate grid shared by regular and seasonal tariffs in both organisation contexts. */
export function ActivityTariffFields({
  form,
  tariffName,
  category,
}: {
  form: FormInstance;
  tariffName: number;
  category: string;
}) {
  const groupRate = ["Transfers", "Private Tours"].includes(category);
  const rates = [
    { name: "costForAdult", label: groupRate ? "Total cost" : "Adult", hidden: false },
    { name: "costForCWB", label: "Children", hidden: groupRate },
    { name: "costForInfant", label: "Infant", hidden: groupRate },
  ];
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-2">
      {rates.map((rate) => (
        <Form.Item
          key={rate.name}
          name={[tariffName, rate.name]}
          label={rate.label}
          rules={[{ type: "number" }]}
          hidden={rate.hidden}
          className="mb-0! min-w-0 [&_.ant-form-item-label]:pb-1!"
        >
          <InputNumber
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            parser={(value) => value?.replace(/\$\s?|(,*)/g, "") || 0}
            placeholder="Not specified"
            aria-label={`${rate.label} tariff`}
            className="w-full!"
          />
        </Form.Item>
      ))}
      <div style={{ display: groupRate ? "none" : "contents" }}><ChildAgeRateFields form={form} tariffName={tariffName} /></div>
    </div>
  );
}
